import { twMerge } from 'tailwind-merge';
import { MoveUpRightIcon } from '../../../../assets/icons/MoveUpRight';
import { TrendingDownIcon } from '../../../../assets/icons/TrendingDown';
import { TrendingUpIcon } from '../../../../assets/icons/TrendingUp';
import { Typography } from '../../../widgets/Typography';
import Button from '../../../widgets/Button';
import { MoveRightIcon } from '../../../../assets/icons/MoveRight';
import { useNavigate } from 'react-router-dom';
import {
  CustomerSuccessManagerEmail,
  IndustryStandardType,
} from '../constants';
import { useFilterData } from '../../../../utilities/hooks/useFilterData';
import useFilters from '../../../../utilities/hooks/useFilters';
import { CircleInfoIcon } from '../../../../assets/icons/CircleInfo';
import AssetURLs from '../../../../shared/constants/assets';

interface Props {
  isAboveBenchmark: boolean;
  belowBenchmarkLabel: string;
  aboveBenchmarkLabel?: string;
  shortVersion?: boolean;
  showIndustryStandardEntryPoint?: boolean;
  ctaLabel?: string;
}

export default function IndustryStandardsAlert({
  isAboveBenchmark,
  belowBenchmarkLabel,
  shortVersion = false,
  showIndustryStandardEntryPoint = false,
  aboveBenchmarkLabel = 'Great going. Your organisation has surpassed the industry standards.',
  ctaLabel = '',
}: Readonly<Props>) {
  const { configData } = useFilterData();
  const { benchmarkType } = useFilters();
  const navigate = useNavigate();

  const selectedBenchmark = configData?.industries.find(
    (industry) => industry.id === benchmarkType,
  );

  const isRelevantSizeFilterApplied = () => {
    const isSizeBenchmark =
      selectedBenchmark?.meta.type === IndustryStandardType.SIZE;
    if (!selectedBenchmark) {
      return true;
    }

    if (!isSizeBenchmark) {
      return true;
    }

    if (selectedBenchmark.meta.type === IndustryStandardType.SIZE) {
      const [min, max] = selectedBenchmark.meta.headCountRange;
      const totalHeadCount = configData?.totalHeadCount || 0;

      return totalHeadCount > min && totalHeadCount <= max;
    }
  };

  const isRelevantSectorFilterApplied = () => {
    const isSectorBenchmark =
      selectedBenchmark?.meta.type === IndustryStandardType.SECTOR;

    if (!selectedBenchmark) {
      return true;
    }

    if (!isSectorBenchmark) {
      return true;
    }

    if (selectedBenchmark.meta.type === IndustryStandardType.SECTOR) {
      return selectedBenchmark.id === configData?.industryId;
    }

    return true;
  };

  const renderInvalidFilterAppliedMessage = () => {
    if (!isRelevantSizeFilterApplied()) {
      return 'For the most relevant insights, compare your performance within your organisation size category.';
    }

    if (!isRelevantSectorFilterApplied()) {
      return 'For the most relevant insights, compare your performance within your industry-specific sector.';
    }

    return '';
  };

  const isRelevantFilterApplied =
    isRelevantSizeFilterApplied() && isRelevantSectorFilterApplied();

  const textSize = shortVersion ? 'body-xs' : 'body-sm';

  const renderContent = () => {
    if (!shortVersion && !isRelevantFilterApplied) {
      return (
        <>
          <CircleInfoIcon className="text-yellow-600" />
          <Typography variant={textSize}>
            {renderInvalidFilterAppliedMessage()}
          </Typography>
          <Button
            variant="link"
            size="sm"
            className="inline p-0 text-left"
            onClick={() => window.open(AssetURLs.IndustryStandardFAQ, '_blank')}
          >
            Learn more about Industry Standards.
            <MoveUpRightIcon className="ml-1 inline-block h-3 shrink-0" />
          </Button>
        </>
      );
    }

    if (isAboveBenchmark) {
      return (
        <>
          <TrendingUpIcon className="h-5 w-5 stroke-green-500" />
          <Typography variant={textSize}>{aboveBenchmarkLabel}</Typography>
          {!shortVersion && (
            <Button
              variant="link"
              size="sm"
              className="inline p-0 text-left"
              onClick={() => window.open('/resourcehub', '_blank')}
            >
              {ctaLabel ||
                'Keep up your momentum by consistently engaging your organisation'}
              <MoveUpRightIcon className="ml-1 inline-block h-3 shrink-0" />
            </Button>
          )}
        </>
      );
    }

    return (
      <>
        <TrendingDownIcon className="stroke-yellow-600" />
        <Typography variant={textSize}>{belowBenchmarkLabel}</Typography>
        {!shortVersion && (
          <Button
            variant="link"
            size="sm"
            className="inline p-0 text-left"
            onClick={() => window.open(`mailto:${CustomerSuccessManagerEmail}`)}
          >
            {ctaLabel ||
              'Reach out to your Customer Success Manager for support'}
            <MoveUpRightIcon className="ml-1 inline-block h-3 shrink-0" />
          </Button>
        )}
      </>
    );
  };

  return (
    <div
      className={twMerge([
        'flex w-fit flex-row items-center justify-between gap-2 rounded-xl border',
        isAboveBenchmark && 'bg-green-25 border-green-100',
        !isAboveBenchmark && 'border-yellow-200 bg-yellow-50',
        shortVersion ? 'px-2 py-1' : 'px-4 py-2',
        !isRelevantFilterApplied && 'border-yellow-200 bg-yellow-50',
      ])}
    >
      <div className="flex flex-col items-start gap-2 md:flex-row md:items-center">
        {renderContent()}
      </div>
      {showIndustryStandardEntryPoint && (
        <Button
          variant="ghost"
          className="!p-0 text-xs text-blue-700"
          rightIcon={<MoveRightIcon className="size-3" />}
          onClick={() => navigate('/d/industry-standards')}
        >
          View
        </Button>
      )}
    </div>
  );
}
